import { PlusOutlined } from "@ant-design/icons";
import { Form, Input, Select, Image, Upload } from "antd";
import type { GetProp, UploadFile, UploadProps } from "antd";
import { useEffect, useState } from "react";
import ReCAPTCHA from "react-google-recaptcha";
import { useTranslation } from "react-i18next";

type ContactFormProps = {};
type FileType = Parameters<GetProp<UploadProps, "beforeUpload">>[0];

const getBase64 = (file: FileType): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = (error) => reject(error);
  });

const ContactForm = (_props: ContactFormProps) => {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const [countries, setCountries] = useState<ICountry[]>([]);

  const contactReasons = [
    {
      value: "Wrong application information",
      label: t("contact.contact_info.reasons.wrong_info"),
    },
    {
      value: "Pay failed",
      label: t("contact.contact_info.reasons.pay_failed"),
    },
    {
      value: "Don't remember the registration code",
      label: t("contact.contact_info.reasons.forget_code"),
    },
    {
      value: "Others",
      label: t("contact.contact_info.reasons.others"),
    },
  ];

  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const handleCaptchaChange = (token: string | null) => {
    setCaptchaToken(token);
  };

  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewImage, setPreviewImage] = useState("");
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const handlePreview = async (file: UploadFile) => {
    if (!file.url && !file.preview) {
      file.preview = await getBase64(file.originFileObj as FileType);
    }

    setPreviewImage(file.url || (file.preview as string));
    setPreviewOpen(true);
  };

  const handleChange: UploadProps["onChange"] = ({ fileList: newFileList }) =>
    setFileList(newFileList);

  const uploadButton = (
    <button style={{ border: 0, background: "none" }} type="button">
      <PlusOutlined />
      <div style={{ marginTop: 8 }}>{t("contact.contact_info.upload")}</div>
    </button>
  );

  const onFinish = (values: any) => {
    console.log("Received values of form: ", { ...values, captchaToken });
  };

  useEffect(() => {
    const fetchCountries = async () => {
      const response = await fetch(
        "https://api.evisa.gov.vn/client-service/public/dm-qt/get-all?type="
      );
      const data = await response.json();

      setCountries(data.data);
    };

    fetchCountries();
  }, []);

  return (
    <div>
      <Form
        layout="vertical"
        form={form}
        onFinish={onFinish}
        className="grid lg:grid-cols-2 gap-4"
      >
        <Form.Item
          label={
            <span className="text-primary-blue font-medium">
              {t("registration_form.step4.emergency_fullname")}
            </span>
          }
          name="fullname"
          rules={[
            {
              required: true,
              message: t("registration_form.empty_emergency_fullname", {
                ns: "message",
              }),
            },
          ]}
        >
          <Input
            placeholder={t("registration_form.step4.emergency_fullname_hint")}
          />
        </Form.Item>

        <Form.Item
          label={
            <span className="text-primary-blue font-medium">
              {t("registration_form.step2.nationality")}
            </span>
          }
          name="nationality"
        >
          <Select
            placeholder={t("registration_form.step2.nationality_hint")}
            showSearch
          >
            {countries.map((country) => (
              <Select.Option key={country.maQT} value={country.tenQTEn}>
                {country.tenQTEn}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item
          label={
            <span className="text-primary-blue font-medium">
              {t("policy.personal_info.collects_info.list3")}
            </span>
          }
          name="phone"
        >
          <Input placeholder={t("registration_form.step6.number_in_vn_hint")} />
        </Form.Item>

        <Form.Item
          label={
            <span className="text-primary-blue font-medium">
              {t("contact.contact_info.email")}
            </span>
          }
          name="email"
          rules={[
            {
              required: true,
              message: t("registration_form.empty_email", {
                ns: "message",
              }),
            },
            {
              type: "email",
              message: t("registration_form.invalid_email", {
                ns: "message",
              }),
            },
          ]}
        >
          <Input placeholder={t("registration_form.step2.email_hint")} />
        </Form.Item>

        <Form.Item
          label={
            <span className="text-primary-blue font-medium">
              {t("registration_form.step3.passport_number")}
            </span>
          }
          name="passport"
        >
          <Input
            placeholder={t("registration_form.step3.passport_number_hint")}
          />
        </Form.Item>

        <Form.Item
          initialValue={"Wrong application information"}
          label={
            <span className="text-primary-blue font-medium">
              {t("apply_visa.visa_type")}
            </span>
          }
          name="type"
        >
          <Select showSearch>
            {contactReasons.map((reason) => (
              <Select.Option key={reason.value} value={reason.value}>
                {reason.label}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item
          label={
            <span className="text-primary-blue font-medium">
              {t("contact.contact_info.description")}
            </span>
          }
          name="description"
          className="lg:col-span-2"
          rules={[
            {
              required: true,
              message: t("contact_form.empty_description", {
                ns: "message",
              }),
            },
          ]}
        >
          <Input.TextArea
            placeholder={t("contact.contact_info.description_hint")}
          />
        </Form.Item>

        <p className="text-primary-blue font-medium lg:col-span-2">
          {t("contact.contact_info.upload_file")}
        </p>
        <Upload
          action="https://660d2bd96ddfa2943b33731c.mockapi.io/api/upload"
          listType="picture-card"
          fileList={fileList}
          onPreview={handlePreview}
          onChange={handleChange}
          className="lg:col-span-2"
          beforeUpload={(file) => {
            const isJpgOrPng =
              file.type === "image/jpeg" || file.type === "image/png";
            const isLt2M = file.size / 1024 / 1024 < 2;

            if (!isJpgOrPng) {
              console.error("You can only upload JPG/PNG file!");
            }

            if (!isLt2M) {
              console.error("Image must smaller than 2MB!");
            }

            return isJpgOrPng && isLt2M;
          }}
        >
          {fileList.length >= 3 ? null : uploadButton}
        </Upload>
        {previewImage && (
          <Image
            wrapperStyle={{ display: "none" }}
            preview={{
              visible: previewOpen,
              onVisibleChange: (visible) => setPreviewOpen(visible),
              afterOpenChange: (visible) => !visible && setPreviewImage(""),
            }}
            src={previewImage}
          />
        )}
        <p className="text-red-500 lg:col-span-2">
          {t("contact.contact_info.upload_note")}
        </p>

        {/* reCAPTCHA */}
        <ReCAPTCHA
          sitekey="6LcOAtcqAAAAAIeFmghNYnIu_-XWagOAO4xtRNQW"
          onChange={handleCaptchaChange}
        />

        {/* Submit Button */}
        <Form.Item className="lg:col-span-2 mt-6">
          <button
            type="submit"
            className="w-full py-4 px-6 font-bold rounded-3xl text-white bg-primary-blue hover:bg-primary-blue/80 focus:outline-none"
          >
            {t("contact.submit")}
          </button>
        </Form.Item>
      </Form>
    </div>
  );
};

export default ContactForm;
